import {
  useIsMobile,
  WSList,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  RequirementStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo, useState } from "react";

import {
  RequirementsListView,
  selectorRequirementsListView
} from "../selectors/selectorRequirementsListView";
import { RequirementContextType, RequirementTableItemData } from "../types";
import { sortRequirementsByPriority } from "../utils";

import { ExtendedRequirementPreviewList } from "./ExtendedRequirementPreviewList";
import { ExtendedRequirementsTable } from "./ExtendedRequirementsTable";
import { RequirementsEmptyState } from "./RequirementsEmptyState";
import { RequirementsTable } from "./RequirementsTable";

export type PayerPayeeDetailsRequirementsProps = {
  noActions?: boolean;
  hasEligibleEngagements: boolean;
  requirements: PayeeRequirement[];
  archivedRequirements: PayeeRequirement[];
  contextType: RequirementContextType;
  goToRequirement: (requirement: PayeeRequirement) => void;
  // TODO(v3-blocker): v1 `PayerPayeeStatus` (Active/Inactive) has no v3 SDK
  // equivalent shared across Payer/Payee contexts (payee status lives on
  // `PayeeStatus`, payer-side status elsewhere, neither shaped the same way).
  // Narrowed to the one bit this component actually reads — whether the
  // relationship is archived — until a real cross-context status source
  // exists.
  isInactive: boolean;
  /**
   * The requirement list read is still in flight.
   *
   * Without it an unresolved list is indistinguishable from an empty one, so the
   * tab rendered the "No requirements active" empty state — and a `0 of 0
   * completed` pill — over a request that had not returned. The sibling
   * `RouteClientsRequirements` screen already takes the same flag.
   */
  isLoading?: boolean;
};

export const PayerPayeeDetailsRequirements: React.FC<
  PayerPayeeDetailsRequirementsProps
> = ({
  hasEligibleEngagements,
  requirements,
  archivedRequirements,
  contextType,
  isInactive,
  goToRequirement,
  noActions,
  isLoading
}) => {
  const isMobile = useIsMobile();
  const [showArchived, setShowArchived] = useState(false);

  const sortedRequirements = useMemo(
    () => sortRequirementsByPriority(requirements),
    [requirements]
  );

  const sortedArchivedRequirements = useMemo(
    () => sortRequirementsByPriority(archivedRequirements),
    [archivedRequirements]
  );

  const completedLength = useMemo(() => {
    return sortedRequirements.filter(
      e => e.status === RequirementStatus.COMPLETED
    ).length;
  }, [sortedRequirements]);

  const isRequirementsComplete = useMemo(() => {
    return sortedRequirements.every(
      e => e.status === RequirementStatus.COMPLETED
    );
  }, [sortedRequirements]);

  const [showPreviews, setShowPreviews] = useState(isMobile);

  // Every item here is already `RequirementTableItemData` (a real
  // `PayeeRequirement`, never a `CustomRequirement`), so it's always
  // routable — no discriminator needed (v1 checked `"requirementId" in
  // requirement`, a field that no longer exists on the v3 model).
  const getOnViewDetails = (_requirement: RequirementTableItemData) => {
    return goToRequirement;
  };

  const isArchivalAvailable = archivedRequirements.length > 0;

  const listView = selectorRequirementsListView({
    count: sortedRequirements.length,
    isLoading: !!isLoading
  });

  return (
    <WSList gap="L">
      <WSSectionToolbar
        buttons={[
          {
            label: "",
            onClick: () => setShowPreviews(true),
            kind: showPreviews ? "Secondary" : "Link",
            icon: "grid",
            onlyIcon: true,
            size: "S"
          },
          {
            label: "",
            onClick: () => setShowPreviews(false),
            kind: !showPreviews ? "Secondary" : "Link",
            icon: "list",
            onlyIcon: true,
            size: "S"
          }
        ]}
        pill={
          isInactive
            ? {
                icon: "archive",
                text: "Archived",
                theme: "neutral"
              }
            : // No completion count until the list has resolved — the counts are
              // both zero while loading, which reads as a real "0 of 0".
              listView === RequirementsListView.Loading
              ? undefined
              : sortedRequirements.length > 0
                ? {
                    icon: true,
                    text: `${completedLength} of ${sortedRequirements.length} completed`,
                    theme:
                      isRequirementsComplete && hasEligibleEngagements
                        ? "success"
                        : "warning"
                  }
                : undefined
        }
        title="Requirements"
      />

      {listView === RequirementsListView.Empty ? (
        <RequirementsEmptyState />
      ) : showPreviews ? (
        <ExtendedRequirementPreviewList
          contextType={contextType}
          getOnViewDetails={getOnViewDetails}
          isLoading={isLoading}
          requirements={sortedRequirements}
        />
      ) : (
        <ExtendedRequirementsTable
          contextType={contextType}
          getOnViewDetails={getOnViewDetails}
          hideActions={noActions}
          isLoading={isLoading}
          requirements={sortedRequirements}
          showEngagementsView={true}
        />
      )}

      {isArchivalAvailable ? (
        <>
          <WSSectionToolbar
            buttons={[
              {
                label: `${showArchived ? "Hide" : "Show"} archived (${
                  archivedRequirements.length
                })`,
                kind: "Link",
                onClick: () => setShowArchived(showArchived => !showArchived)
              }
            ]}
            title={showArchived ? "Archived Requirements" : ""}
          />
          {showArchived ? (
            <RequirementsTable
              contextType={contextType}
              requirements={sortedArchivedRequirements}
              onViewDetails={
                contextType === RequirementContextType.Payee
                  ? goToRequirement
                  : undefined
              }
            />
          ) : null}
        </>
      ) : null}
    </WSList>
  );
};
